/**
 * TanStack Query bindings.
 *
 * Every key starts with the project id. That is not tidiness — switching
 * projects has to invalidate everything, and a key that omits the project would
 * serve one tenant's cached list while the header says another. Making the
 * project part of the key means the cache cannot lie about whose data it holds.
 */

import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';
import { api, getCurrentProject } from './client';
import type {
  ApiKey, AuditEvent, Delivery, Integration, Journey, JourneyDetail,
  ListResponse, Message, MessageFunnel, Overview, Preference, Profile,
  ProfileDetail, Project, Segment, SegmentNode, SegmentPreview, Suppression,
  TeamMember, Template, TrackedEvent, User,
} from './types';

const project = () => getCurrentProject() ?? 'none';

export const keys = {
  me: ['me'] as const,
  projects: ['projects'] as const,
  project: () => ['project', project()] as const,
  overview: (days: number) => ['overview', project(), days] as const,
  profiles: (cursor?: string) => ['profiles', project(), cursor ?? ''] as const,
  profile: (id: string) => ['profile', project(), id] as const,
  messages: () => ['messages', project()] as const,
  message: (id: string) => ['message', project(), id] as const,
  deliveries: (id: string) => ['deliveries', project(), id] as const,
  funnel: (id: string) => ['funnel', project(), id] as const,
  templates: (channel?: string) => ['templates', project(), channel ?? ''] as const,
  template: (id: string) => ['template', project(), id] as const,
  segments: () => ['segments', project()] as const,
  segment: (id: string) => ['segment', project(), id] as const,
  journeys: () => ['journeys', project()] as const,
  journey: (id: string) => ['journey', project(), id] as const,
  integrations: () => ['integrations', project()] as const,
  apiKeys: () => ['api-keys', project()] as const,
  audit: (filters: string) => ['audit', project(), filters] as const,
  suppressions: (channel?: string) => ['suppressions', project(), channel ?? ''] as const,
  team: () => ['team', project()] as const,
  eventNames: () => ['event-names', project()] as const,
  events: (name?: string) => ['events', project(), name ?? ''] as const,
  preferences: (id: string) => ['preferences', project(), id] as const,
};

// ── Session ───────────────────────────────────────────────────────

export const useMe = (options?: Partial<UseQueryOptions<User>>) =>
  useQuery({
    queryKey: keys.me,
    queryFn: () => api.get<User>('/v1/me', { noProject: true }),
    ...options,
  });

export const useMyProjects = () =>
  useQuery({
    queryKey: keys.projects,
    queryFn: async () =>
      (await api.get<ListResponse<Project>>('/v1/me/projects', { noProject: true })).data,
  });

export const useProject = () =>
  useQuery({
    queryKey: keys.project(),
    queryFn: () => api.get<Project>('/v1/project'),
    enabled: !!getCurrentProject(),
  });

// ── Analytics ─────────────────────────────────────────────────────

export const useOverview = (days = 7) =>
  useQuery({
    queryKey: keys.overview(days),
    queryFn: () => api.get<Overview>(`/v1/analytics/overview?days=${days}`),
    enabled: !!getCurrentProject(),
    // The overview is the landing view and reads from live tables, so a short
    // stale window keeps it feeling current without hammering the database on
    // every tab focus.
    staleTime: 30_000,
  });

export const useEventNames = () =>
  useQuery({
    queryKey: keys.eventNames(),
    queryFn: async () =>
      (await api.get<ListResponse<string>>('/v1/analytics/event-names')).data,
    staleTime: 5 * 60_000,
  });

export const useEvents = (name?: string) =>
  useInfiniteQuery({
    queryKey: keys.events(name),
    queryFn: async ({ pageParam }) => {
      const qs = new URLSearchParams();
      if (name) qs.set('name', name);
      if (pageParam) qs.set('cursor', pageParam);
      const q = qs.toString();
      return api.get<ListResponse<TrackedEvent>>(`/v1/events${q ? `?${q}` : ''}`);
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
  });

// ── Profiles ──────────────────────────────────────────────────────

export const useProfiles = (cursor?: string) =>
  useQuery({
    queryKey: keys.profiles(cursor),
    queryFn: async () => {
      const qs = cursor ? `?cursor=${encodeURIComponent(cursor)}` : '';
      return api.get<ListResponse<Profile>>(`/v1/profiles${qs}`);
    },
  });

export const useProfileDetail = (id: string) =>
  useQuery({
    queryKey: keys.profile(id),
    queryFn: () => api.get<ProfileDetail>(`/v1/profiles/${id}/detail`),
    enabled: !!id,
  });

export const usePreferences = (id: string) =>
  useQuery({
    queryKey: keys.preferences(id),
    queryFn: async () =>
      (await api.get<ListResponse<Preference>>(`/v1/profiles/${id}/preferences`)).data,
    enabled: !!id,
  });

export function useDeleteProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del(`/v1/profiles/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['profiles'] }),
  });
}

// ── Messages ──────────────────────────────────────────────────────

export const useMessages = () =>
  useQuery({
    queryKey: keys.messages(),
    queryFn: async () => (await api.get<ListResponse<Message>>('/v1/messages')).data,
  });

export const useMessage = (id: string, poll = false) =>
  useQuery({
    queryKey: keys.message(id),
    queryFn: () => api.get<Message>(`/v1/messages/${id}`),
    enabled: !!id,
    // A send is asynchronous — the API returns 202 and fan-out happens in a
    // worker — so the detail view polls until the message reaches a terminal
    // state, then stops.
    refetchInterval: (query) => {
      if (!poll) return false;
      const status = query.state.data?.status;
      return status === 'queued' || status === 'sending' ? 2000 : false;
    },
  });

export const useDeliveries = (id: string) =>
  useQuery({
    queryKey: keys.deliveries(id),
    queryFn: async () =>
      (await api.get<ListResponse<Delivery>>(`/v1/messages/${id}/deliveries?limit=200`)).data,
    enabled: !!id,
  });

export const useFunnel = (id: string) =>
  useQuery({
    queryKey: keys.funnel(id),
    queryFn: () => api.get<MessageFunnel>(`/v1/messages/${id}/funnel`),
    enabled: !!id,
  });

export interface SendInput {
  channel: string;
  category?: string;
  audience: Record<string, unknown>;
  push?: Record<string, unknown>;
  template_id?: string;
  data?: Record<string, unknown>;
  priority?: number;
  dry_run?: boolean;
  scheduled_at?: string;
}

export function useSendMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: SendInput) =>
      api.post<Message>('/v1/messages', input, {
        // A fresh key per attempt: retrying a FAILED submit should send, while
        // a double-click within one attempt should not. crypto.randomUUID is
        // available in every browser this dashboard supports.
        idempotencyKey: crypto.randomUUID(),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['messages'] }),
  });
}

// ── Templates ─────────────────────────────────────────────────────

export const useTemplates = (channel?: string) =>
  useQuery({
    queryKey: keys.templates(channel),
    queryFn: async () => {
      const qs = channel ? `?channel=${channel}` : '';
      return (await api.get<ListResponse<Template>>(`/v1/templates${qs}`)).data;
    },
  });

export const useTemplate = (id: string) =>
  useQuery({
    queryKey: keys.template(id),
    queryFn: () => api.get<Template>(`/v1/templates/${id}`),
    enabled: !!id && id !== 'new',
  });

export function useSaveTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Record<string, unknown>) => api.put<Template>('/v1/templates', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['templates'] }),
  });
}

export function useDeleteTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del(`/v1/templates/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['templates'] }),
  });
}

export interface TemplatePreview {
  subject: string;
  text: string;
  html: string;
  sms_segments?: number;
  sms_unicode?: boolean;
  sms_length?: number;
}

export function usePreviewTemplate() {
  return useMutation({
    mutationFn: (input: Record<string, unknown>) =>
      api.post<TemplatePreview>('/v1/templates/preview', input),
  });
}

export function useSetDLTStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string } & Record<string, unknown>) =>
      api.put<Template>(`/v1/templates/${id}/dlt`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['templates'] }),
  });
}

// ── Segments ──────────────────────────────────────────────────────

export const useSegments = () =>
  useQuery({
    queryKey: keys.segments(),
    queryFn: async () => (await api.get<ListResponse<Segment>>('/v1/segments')).data,
  });

export const useSegment = (id: string) =>
  useQuery({
    queryKey: keys.segment(id),
    queryFn: () => api.get<Segment>(`/v1/segments/${id}`),
    enabled: !!id && id !== 'new',
  });

export function useSaveSegment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Record<string, unknown>) => api.put<Segment>('/v1/segments', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['segments'] }),
  });
}

export function useDeleteSegment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del(`/v1/segments/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['segments'] }),
  });
}

export function usePreviewSegment() {
  return useMutation({
    mutationFn: (definition: SegmentNode) =>
      api.post<SegmentPreview>('/v1/segments/preview', { definition, limit: 20 }),
  });
}

// ── Journeys ──────────────────────────────────────────────────────

export const useJourneys = () =>
  useQuery({
    queryKey: keys.journeys(),
    queryFn: async () => (await api.get<ListResponse<Journey>>('/v1/journeys')).data,
  });

export const useJourney = (id: string) =>
  useQuery({
    queryKey: keys.journey(id),
    queryFn: () => api.get<JourneyDetail>(`/v1/journeys/${id}`),
    enabled: !!id && id !== 'new',
  });

export function useSaveJourney() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id?: string } & Record<string, unknown>) =>
      id ? api.put<Journey>(`/v1/journeys/${id}`, body) : api.post<Journey>('/v1/journeys', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['journeys'] }),
  });
}

export function useSetJourneyStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      api.put<Journey>(`/v1/journeys/${id}/status`, { status }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['journeys'] });
      qc.invalidateQueries({ queryKey: ['journey'] });
    },
  });
}

export function useDeleteJourney() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del(`/v1/journeys/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['journeys'] }),
  });
}

// ── Settings ──────────────────────────────────────────────────────

export const useIntegrations = () =>
  useQuery({
    queryKey: keys.integrations(),
    queryFn: async () => (await api.get<ListResponse<Integration>>('/v1/integrations')).data,
  });

export function useSaveIntegration() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Record<string, unknown>) =>
      api.put<Integration>('/v1/integrations', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['integrations'] }),
  });
}

export function useVerifyIntegration() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.post<{ verified: boolean; error?: { code: string; message: string } }>(
        `/v1/integrations/${id}/verify`,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['integrations'] }),
  });
}

export function useDeleteIntegration() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del(`/v1/integrations/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['integrations'] }),
  });
}

export const useApiKeys = () =>
  useQuery({
    queryKey: keys.apiKeys(),
    queryFn: async () => (await api.get<ListResponse<ApiKey>>('/v1/api-keys')).data,
  });

export function useCreateApiKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { name: string; scope: string }) =>
      api.post<ApiKey>('/v1/api-keys', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['api-keys'] }),
  });
}

export function useRevokeApiKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del(`/v1/api-keys/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['api-keys'] }),
  });
}

export function useSaveBranding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string } & Record<string, unknown>) =>
      api.put<Project>(`/v1/projects/${id}/branding`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['project'] }),
  });
}

export function useSaveSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string } & Record<string, unknown>) =>
      api.put<Project>(`/v1/projects/${id}/settings`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['project'] }),
  });
}

export function useCreateProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Record<string, unknown>) =>
      api.post<Project>('/v1/projects', input, { noProject: true }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['projects'] }),
  });
}

// ── Governance ────────────────────────────────────────────────────

export const useAudit = (filters: { action?: string; actor_id?: string }) => {
  const qs = new URLSearchParams();
  if (filters.action) qs.set('action', filters.action);
  if (filters.actor_id) qs.set('actor_id', filters.actor_id);
  const query = qs.toString();

  return useQuery({
    queryKey: keys.audit(query),
    queryFn: async () =>
      (await api.get<ListResponse<AuditEvent>>(`/v1/audit${query ? `?${query}` : ''}`)).data,
  });
};

export const useSuppressions = (channel?: string) =>
  useQuery({
    queryKey: keys.suppressions(channel),
    queryFn: async () => {
      const qs = channel ? `?channel=${channel}` : '';
      return (await api.get<ListResponse<Suppression>>(`/v1/suppressions${qs}`)).data;
    },
  });

export function useAddSuppression() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { channel: string; address: string; reason?: string }) =>
      api.post('/v1/suppressions', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['suppressions'] }),
  });
}

export function useDeleteSuppression() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ channel, address }: { channel: string; address: string }) =>
      api.del(
        `/v1/suppressions?channel=${encodeURIComponent(channel)}&address=${encodeURIComponent(address)}`,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['suppressions'] }),
  });
}

export const useTeam = () =>
  useQuery({
    queryKey: keys.team(),
    queryFn: async () => (await api.get<ListResponse<TeamMember>>('/v1/team')).data,
  });

export function useInviteMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { email: string; password: string; name: string; role: string }) =>
      api.post('/v1/team', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['team'] }),
  });
}

export function useRemoveMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del(`/v1/team/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['team'] }),
  });
}
